import { useEffect, useState } from 'react';
import { useUrlFirmada } from '@/hooks/useUrlFirmada';
import {
  metodoConsultaTipo,
  tipoVisorPorContentType,
  tipoVisorPorExtension,
  type TipoVisor,
} from '@/lib/tipoArchivoVisor';

// Una URL de evidencia no cambia de tipo: se pregunta una vez por sesión. Se guarda la promesa
// para que dos visores de la misma URL (panel + visor) no hagan dos consultas.
const cache = new Map<string, Promise<TipoVisor>>();

function consultarTipo(url: string, metodo: 'HEAD' | 'GET', respaldo: TipoVisor): Promise<TipoVisor> {
  let p = cache.get(url);
  if (!p) {
    p = fetch(url, { method: metodo })
      .then((r) => (r.ok && tipoVisorPorContentType(r.headers.get('content-type'))) || respaldo)
      .catch(() => {
        cache.delete(url); // error de red: se reintenta la próxima vez
        return respaldo;
      });
    cache.set(url, p);
  }
  return p;
}

/**
 * Tipo de visor para `url`: por extensión (de la URL o del `nombre`) y, si no alcanza, por el
 * `Content-Type` del servidor (HEAD, sin bajar el archivo). Mientras pregunta devuelve `null`.
 * Si el servidor no responde o no dice nada útil, `'otro'`: el visor nativo, como antes.
 */
export function useTipoArchivoVisor(url?: string | null, nombre?: string | null): TipoVisor | null {
  const porExtension = tipoVisorPorExtension(url, nombre);
  const resuelto = porExtension === 'imagen' || porExtension === 'pdf';
  const [consultado, setConsultado] = useState<{ url: string; tipo: TipoVisor } | null>(null);
  // La consulta al servidor va con URL firmada: con el bucket privado la pública no responde.
  // Si el tipo ya se resolvió por extensión no hace falta firmar nada.
  const { url: firmada } = useUrlFirmada(resuelto ? null : url);

  useEffect(() => {
    if (!url || resuelto || !firmada) return;
    const metodo = metodoConsultaTipo(firmada);
    const respaldo: TipoVisor = porExtension ?? 'otro';
    if (!metodo) {
      setConsultado({ url, tipo: respaldo });
      return;
    }
    let vigente = true;
    void consultarTipo(firmada, metodo, respaldo).then((tipo) => {
      if (vigente) setConsultado({ url, tipo });
    });
    return () => {
      vigente = false;
    };
  }, [url, firmada, resuelto, porExtension]);

  if (!url) return null;
  if (resuelto) return porExtension;
  return consultado?.url === url ? consultado.tipo : null;
}
